import { resolveId } from '@medplum/core'
import {
  endOfDateInLocalTimezone,
  startOfDateInLocalTimezone,
  today,
} from '@seen/core/dates'
import { ExtendedAllergy } from '@seen/core/models/allergies'
import { MealTicketServing } from '@seen/core/models/mealTicketServing'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import {
  Avatar,
  Loader,
  PatientName,
  Toaster,
  useAppointments,
  useParallelSearchOne,
  useSearch,
} from '@seen/react'
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AlphabetIndex } from '#src/AlphabetIndex.tsx'
import { SEEN_API_URL } from '#src/config.ts'
import {
  availableLetters,
  filterParticipantsByQuery,
  indexLetterFor,
  sortParticipantsByName,
} from '#src/participantList.ts'
import { useServedParticipants } from '#src/useServedParticipants.ts'

/*
 * Escapes HTML special characters to prevent XSS attacks
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function MealTicketPrinter(): JSX.Element {
  const [selectedPatient, setSelectedPatient] = useState<Participant | null>(
    null
  )
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showServeLeadModal, setShowServeLeadModal] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [activeTab, setActiveTab] = useState('daily')

  // Fetch participants from Medplum
  const { data: participants, isLoading } = useSearch(
    Participant,
    Participant.enrolledSearchQuery()
  )

  // Fetch today's appointments to filter participants
  const todaysDate = today()
  const { start, end } = useMemo(
    () => ({
      start: startOfDateInLocalTimezone(todaysDate),
      end: endOfDateInLocalTimezone(todaysDate),
    }),
    [todaysDate]
  )

  const appointmentsResult = useAppointments({
    start,
    end,
    statuses: ['booked'],
  })

  // Get participant IDs who have appointments today
  const participantIdsWithAppointments = useMemo(() => {
    const appointments =
      appointmentsResult.data?.filter((a) => !!a.patient) ?? []
    return new Set(
      appointments.map((appointment) => resolveId(appointment.patient))
    )
  }, [appointmentsResult.data])

  const filteredParticipants = useMemo(() => {
    if (!participants) {
      return []
    }

    if (activeTab === 'daily') {
      // Filter to only participants who have appointments today
      return participants.filter(
        (p) => p.isActive && participantIdsWithAppointments.has(p.id)
      )
    }

    return participants.filter((p) => p.isActive)
  }, [participants, activeTab, participantIdsWithAppointments])

  const [searchQuery, setSearchQuery] = useState('')
  const [mealType, setMealType] = useState(() =>
    MealTicketServing.currentMealType()
  )

  useEffect(() => {
    const tick = (): void => {
      const next = MealTicketServing.currentMealType()
      setMealType((prev) => (prev === next ? prev : next))
    }
    let timeoutId: ReturnType<typeof setTimeout>
    const arm = (): void => {
      timeoutId = setTimeout(() => {
        tick()
        arm()
      }, MealTicketServing.msUntilNextMealChange())
    }
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        tick()
        clearTimeout(timeoutId)
        arm()
      }
    }
    arm()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  // Alphabetize (by last name) and apply the name search. Sorting after
  // filtering keeps the A-Z rail aligned with what's shown.
  const displayedParticipants = useMemo(
    () =>
      sortParticipantsByName(
        filterParticipantsByQuery(filteredParticipants, searchQuery)
      ),
    [filteredParticipants, searchQuery]
  )
  const lettersWithParticipants = useMemo(
    () => availableLetters(displayedParticipants),
    [displayedParticipants]
  )

  // Shared "served today" status for the current meal (from local time),
  // synced across every iPad (replaces the old per-device localStorage copy).
  const served = useServedParticipants(mealType)
  const [ticketCounter, setTicketCounter] = useState(1)
  const [specialRequests, setSpecialRequests] = useState('')

  const handleJumpToLetter = useCallback((letter: string): void => {
    document
      .getElementById(`ml-letter-${letter}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  // Fetch diet questionnaire response for selected participant
  const dietQuestionnaireQuery = useMemo(
    () =>
      selectedPatient
        ? [
            {
              resourceClass: ExtendedQuestionnaireResponse,
              query: ExtendedQuestionnaireResponse.searchQuery({
                questionnaire: questionnaires.DietQuestionnaire,
                subject: { reference: `Patient/${selectedPatient.id}` },
              }),
            },
          ]
        : [],
    [selectedPatient]
  )

  const [dietQuestionnaireResult] = useParallelSearchOne(
    dietQuestionnaireQuery,
    {
      query: {
        enabled: !!selectedPatient,
      },
    }
  )

  // Fetch allergies for selected participant
  const { data: allergies, isLoading: allergiesLoading } = useSearch(
    ExtendedAllergy,
    selectedPatient
      ? {
          patient: selectedPatient.reference,
          'clinical-status': 'active',
        }
      : {},
    {
      query: {
        enabled: !!selectedPatient,
      },
    }
  )

  // Check if patient data is still loading (prevent race condition when printing)
  const isLoadingPatientData =
    !!selectedPatient &&
    (allergiesLoading || dietQuestionnaireResult?.isLoading)

  // Generate ticket HTML content
  const getTicketContent = useCallback(
    (participant: Participant): string => {
      const dietResponse = dietQuestionnaireResult?.data

      const diet = dietResponse?.getAnswerValue('diet')
      const fluidConsistency = dietResponse?.getAnswerValue('fluid-consistency')
      const restrictions = dietResponse?.getAnswerValue('fluid-restrictions')
      const supplements = dietResponse?.getAnswerValue('supplements')
      const dislikes = dietResponse?.getAnswerValue('dislikes')
      const notes = dietResponse?.getAnswerValue('notes')

      // Convert values to strings and handle null/undefined
      const dietStr = diet ? String(diet) : ''
      const fluidConsistencyStr = fluidConsistency
        ? String(fluidConsistency)
        : ''
      const restrictionsStr = restrictions ? String(restrictions) : ''
      const supplementsStr = supplements ? String(supplements) : ''
      const dislikesStr = dislikes ? String(dislikes) : ''
      const notesStr = notes ? String(notes) : ''

      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
  <title>Meal Ticket - ${escapeHtml(participant.name)}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "SimHei", "STHeiti", "WenQuanYi Micro Hei", sans-serif;
      padding: 40px;
      max-width: 800px;
      margin: 0 auto;
      font-size: 24pt;
      background-color: white;
      color: black;
    }
    h1 {
      font-size: 36pt;
      margin-bottom: 20px;
      font-weight: 700;
    }
    .participant-info {
      margin-bottom: 30px;
      font-size: 28pt;
    }
    .section {
      border: 2px solid #e5e5e5;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 30px;
    }
    .section-title {
      font-size: 28pt;
      margin-top: 0;
      margin-bottom: 20px;
      color: #1a1a1a;
      font-weight: 700;
    }
    .item {
      margin-bottom: 16px;
    }
    .label {
      font-weight: bold;
    }
    .timestamp {
      font-size: 20pt;
      color: #666;
    }
    .allergy-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    .allergy-item {
      padding: 8px 0;
      border-bottom: 1px solid #e5e5e5;
    }
    .allergy-item:last-child {
      border-bottom: none;
    }
    .item ul {
      list-style: disc;
      padding-left: 24px;
      margin: 0;
    }
    .item ol {
      list-style: decimal;
      padding-left: 24px;
      margin: 0;
    }
    .item li {
      margin-bottom: 8px;
    }
    .item p {
      margin: 0;
    }
    .item strong {
      font-weight: bold;
    }
    .item em {
      font-style: italic;
    }

    @media print {
      * {
        print-color-adjust: exact !important;
        -webkit-print-color-adjust: exact !important;
      }
    }
  </style>
</head>
<body>
  <h1>Seen Health SGV 餐票</h1>
  <div class="participant-info">
    <div><b>${escapeHtml(participant.name)}</b></div>
  </div>
  <div class="section">
    <h2 class="section-title">Allergies 过敏</h2>
    ${
      allergies && allergies.length > 0
        ? `<ul class="allergy-list">
      ${allergies.map((allergy) => `<li class="allergy-item">${escapeHtml(allergy.name)}</li>`).join('\n      ')}
    </ul>`
        : '<div>No active allergies listed 没有已知过敏</div>'
    }
  </div>
  <div class="section">
    <h2 class="section-title">Dietary Needs 飲食需求</h2>
    ${dietStr ? `<div class="item"><span class="label">Diet 飲食:</span> ${dietStr}</div>` : ''}
    ${fluidConsistencyStr ? `<div class="item"><span class="label">Fluid Consistency 流体稠度:</span> ${fluidConsistencyStr}</div>` : ''}
    ${restrictionsStr && restrictionsStr.toLowerCase() !== 'none' ? `<div class="item"><span class="label">Restrictions 限制:</span> ${restrictionsStr}</div>` : ''}
    ${supplementsStr && supplementsStr.toLowerCase() !== 'none' ? `<div class="item"><span class="label">Supplements 补充:</span> ${supplementsStr}</div>` : ''}
  </div>
  ${
    dislikesStr
      ? `<div class="section">
    <h2 class="section-title">Dislikes 不喜欢的食物</h2>
    <div class="item">${dislikesStr}</div>
  </div>`
      : ''
  }
  ${
    notesStr
      ? `<div class="section">
    <h2 class="section-title">Notes 备注</h2>
    <div class="item">${notesStr}</div>
  </div>`
      : ''
  }
  <div class="timestamp">
    Printed on: ${new Date().toLocaleString()} 打印时间
  </div>
</body>
</html>`
    },
    [dietQuestionnaireResult?.data, allergies]
  )

  const handlePatientClick = (participant: Participant): void => {
    setSelectedPatient(participant)
    setShowConfirmModal(true)
  }

  const handleConfirmPrint = async (): Promise<void> => {
    if (!selectedPatient) {
      return
    }

    // oxlint-disable-next-line no-console -- debug logging
    console.log(
      `Printing meal ticket #${ticketCounter} for: ${selectedPatient.name}`
    )

    // Send to Star printer
    const ticketHtml = getTicketContent(selectedPatient)
    // oxlint-disable-next-line no-console -- debug logging
    console.log('Ticket HTML:', ticketHtml)

    try {
      const response = await fetch(`${SEEN_API_URL}/printer/meal-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ticketHtml }),
      })

      if (!response.ok) {
        throw new Error(`Printer API error: ${response.status}`)
      }

      // oxlint-disable-next-line no-console -- debug logging
      console.log('Printer API response:', await response.json())
    } catch (error) {
      console.error('Failed to send to printer:', error)
      toast.error('Failed to send to printer')
      return
    }

    // Record served status in shared cross-device state (idempotent). A failure
    // here shouldn't block the print that already succeeded.
    try {
      await served.markServed(selectedPatient)
    } catch (error) {
      console.error('Failed to record served status:', error)
    }

    // Show success message
    toast.success(
      `Meal ticket #${ticketCounter} printed for ${selectedPatient.name}`
    )

    // Increment the counter
    setTicketCounter((prev) => prev + 1)

    // Close modal and reset
    setShowConfirmModal(false)
    setSelectedPatient(null)
  }

  const handleCancelPrint = (): void => {
    setShowConfirmModal(false)
    setSelectedPatient(null)
  }

  const handleSpecialMealClick = (): void => {
    setShowServeLeadModal(true)
  }

  const handleConfirmSpecialPrint = async (): Promise<void> => {
    const requestsText = specialRequests
      ? ` - Special requests: ${specialRequests}`
      : ''
    // oxlint-disable-next-line no-console -- debug logging
    console.log(`Printing special meal ticket #${ticketCounter}${requestsText}`)

    // Generate simple HTML content for special guest ticket
    const ticketHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
  <title>Meal Ticket - Special Guest</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "SimHei", "STHeiti", "WenQuanYi Micro Hei", sans-serif;
      padding: 40px;
      max-width: 800px;
      margin: 0 auto;
      font-size: 24pt;
      background-color: white;
      color: black;
    }
    h1 {
      font-size: 36pt;
      margin-bottom: 20px;
      font-weight: 700;
    }
    .guest-info {
      margin-bottom: 30px;
      font-size: 28pt;
    }
    .section {
      border: 2px solid #e5e5e5;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 30px;
    }
    .section-title {
      font-size: 28pt;
      margin-top: 0;
      margin-bottom: 20px;
      color: #1a1a1a;
      font-weight: 700;
    }
    .item {
      margin-bottom: 16px;
    }
    .timestamp {
      font-size: 20pt;
      color: #666;
    }

    @media print {
      * {
        print-color-adjust: exact !important;
        -webkit-print-color-adjust: exact !important;
      }
    }
  </style>
</head>
<body>
  <h1>Seen Health SGV 餐票</h1>
  <div class="guest-info">
    <div><b>Special Guest / 特别嘉宾</b></div>
  </div>
  ${
    specialRequests
      ? `<div class="section">
    <h2 class="section-title">Special Requests 特殊要求</h2>
    <div class="item">${escapeHtml(specialRequests)}</div>
  </div>`
      : ''
  }
  <div class="timestamp">
    Printed on: ${new Date().toLocaleString()} 打印时间
  </div>
</body>
</html>`

    // oxlint-disable-next-line no-console -- debug logging
    console.log('Special ticket HTML:', ticketHtml)

    try {
      const response = await fetch(`${SEEN_API_URL}/printer/meal-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ticketHtml }),
      })

      if (!response.ok) {
        throw new Error(`Printer API error: ${response.status}`)
      }

      // oxlint-disable-next-line no-console -- debug logging
      console.log('Printer API response:', await response.json())
    } catch (error) {
      console.error('Failed to send to printer:', error)
      toast.error('Failed to send to printer')
      return
    }

    // Show success message
    toast.success(`Meal ticket #${ticketCounter} printed for special guest`)

    // Increment the counter
    setTicketCounter((prev) => prev + 1)

    // Clear special requests and close modal
    setSpecialRequests('')
    setShowServeLeadModal(false)
  }

  const handleCancelSpecialPrint = (): void => {
    setSpecialRequests('')
    setShowServeLeadModal(false)
  }

  return (
    <div className="min-h-screen bg-frame">
      {/* Main Content */}
      <div className="bg-background p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-6 max-w-(--container-prose) font-serif text-4xl font-bold">
            Meal Ticket Printer
          </h1>

          {/* Header Controls - Toggle and Serve Lead Button */}
          <div className="flex max-w-4xl items-center justify-between">
            {/* Styled Toggle */}
            <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground">
              <button
                type="button"
                onClick={() => setActiveTab('daily')}
                className={`inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-background transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 ${
                  activeTab === 'daily'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'hover:bg-background/50 hover:text-foreground'
                }`}
              >
                Daily Attendance
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('all')}
                className={`inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-background transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 ${
                  activeTab === 'all'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'hover:bg-background/50 hover:text-foreground'
                }`}
              >
                All Participants
              </button>
            </div>

            {/* Header Buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                className="relative inline-flex h-10 items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium whitespace-nowrap shadow-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                onClick={() => setShowResetModal(true)}
              >
                Reset
              </button>
              <button
                type="button"
                className="relative inline-flex h-10 items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium whitespace-nowrap shadow-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                onClick={handleSpecialMealClick}
              >
                Special Guest
              </button>
            </div>
          </div>

          <div className="mt-6 max-w-4xl">
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name…"
              className="h-12 w-full rounded-md border border-input bg-background px-4 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
            />
          </div>
        </div>

        {/* Loading State */}
        {(isLoading || appointmentsResult.isLoading || served.isLoading) && (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        )}

        {/* Participants Grid + A-Z jump rail */}
        {!isLoading && !appointmentsResult.isLoading && !served.isLoading && (
          <div className="flex max-w-4xl items-start gap-2">
            <div className="grid flex-1 grid-cols-3 gap-6">
              {displayedParticipants.map((participant, index) => {
                const isPrinted = served.servedIds.has(participant.id)
                const letter = indexLetterFor(participant)
                // Anchor the first card of each letter group so the rail can
                // scroll to it.
                const isFirstOfLetter =
                  index === 0 ||
                  indexLetterFor(displayedParticipants[index - 1]) !== letter
                return (
                  <div
                    key={participant.id}
                    id={isFirstOfLetter ? `ml-letter-${letter}` : undefined}
                    className={`cursor-pointer scroll-mt-4 rounded-lg border p-6 shadow-sm transition-shadow hover:shadow-md ${
                      isPrinted
                        ? 'border-transparent bg-seen-green text-white'
                        : 'border-border bg-card text-card-foreground'
                    }`}
                    onClick={() => handlePatientClick(participant)}
                  >
                    <div className="flex flex-col items-center text-center">
                      <div
                        className={`mb-4 aspect-square w-full ${isPrinted ? 'rounded-full bg-white' : ''}`}
                      >
                        <Avatar
                          profile={participant.reference}
                          className="size-full text-5xl [&_img]:object-cover"
                        />
                      </div>
                      <h3
                        className={`text-lg font-semibold ${
                          isPrinted ? 'text-white' : 'text-card-foreground'
                        }`}
                      >
                        <PatientName participant={participant} />
                      </h3>
                    </div>
                  </div>
                )
              })}
              {displayedParticipants.length === 0 && (
                <div className="col-span-3 py-12 text-center text-muted-foreground">
                  {searchQuery
                    ? `No participants match “${searchQuery}”`
                    : 'No participants to show'}
                </div>
              )}
            </div>
            {displayedParticipants.length > 0 && (
              <AlphabetIndex
                available={lettersWithParticipants}
                onSelect={handleJumpToLetter}
                className="sticky top-4 self-start"
              />
            )}
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && selectedPatient && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
            <div className="mx-4 w-full max-w-md rounded-lg border bg-popover text-popover-foreground shadow-md outline-hidden">
              <div className="flex flex-col gap-3 px-6 pt-2 pb-6">
                <h2 className="text-center text-xl font-semibold">
                  Print Meal Ticket
                </h2>

                <div className="py-4 text-center">
                  <div className="flex flex-col items-center">
                    <div className="mb-4 h-20 w-20 overflow-hidden rounded-full bg-muted">
                      <Avatar
                        profile={selectedPatient.reference}
                        className="size-full text-2xl [&_img]:object-cover"
                      />
                    </div>
                    {served.servedIds.has(selectedPatient.id) ? (
                      <>
                        <p className="mb-2 text-lg text-orange-600">
                          Ticket already printed for this participant.
                        </p>
                        <p className="text-lg">
                          Are you sure you want to print another?
                        </p>
                        <p className="mt-2 text-xl font-semibold text-primary">
                          {selectedPatient.name}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="mb-2 text-lg">Print meal ticket for</p>
                        <p className="text-xl font-semibold text-primary">
                          {selectedPatient.name}?
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {isLoadingPatientData && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader />
                    <span>Loading dietary info...</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelPrint}
                    className="relative inline-flex h-10 flex-1 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium whitespace-nowrap shadow-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmPrint}
                    disabled={isLoadingPatientData}
                    className="relative inline-flex h-10 flex-1 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium whitespace-nowrap text-primary-foreground shadow ring-offset-background transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                  >
                    {served.servedIds.has(selectedPatient.id)
                      ? 'Yes, print another ticket'
                      : 'Yes, Print'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Serve Lead Confirmation Modal */}
        {showServeLeadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
            <div className="mx-4 w-full max-w-md rounded-lg border bg-popover text-popover-foreground shadow-md outline-hidden">
              <div className="flex flex-col gap-3 px-6 pt-2 pb-6">
                <h2 className="text-center text-xl font-semibold">
                  Special Guest
                </h2>

                <div className="space-y-4 py-4">
                  <p className="text-center text-lg">
                    Print ticket for special guest?
                  </p>

                  <div className="space-y-2">
                    <label
                      htmlFor="special-requests"
                      className="text-sm font-medium text-foreground"
                    >
                      Special requests:
                    </label>
                    <textarea
                      id="special-requests"
                      value={specialRequests}
                      onChange={(e) => setSpecialRequests(e.target.value)}
                      placeholder="Enter any special dietary requirements or requests..."
                      className="flex min-h-[80px] w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelSpecialPrint}
                    className="relative inline-flex h-10 flex-1 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium whitespace-nowrap shadow-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmSpecialPrint}
                    className="relative inline-flex h-10 flex-1 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium whitespace-nowrap text-primary-foreground shadow ring-offset-background transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                  >
                    Yes, Print
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Confirmation Modal */}
        {showResetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
            <div className="mx-4 w-full max-w-md rounded-lg border bg-popover text-popover-foreground shadow-md outline-hidden">
              <div className="flex flex-col gap-3 px-6 pt-2 pb-6">
                <h2 className="text-center text-xl font-semibold">
                  Reset Served Participants
                </h2>

                <div className="py-4 text-center">
                  <p className="text-lg">
                    Are you sure you want to reset all served participants?
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    This will clear the teal background status for all
                    participants.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowResetModal(false)}
                    className="relative inline-flex h-10 flex-1 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium whitespace-nowrap shadow-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetModal(false)
                      void served
                        .resetAll()
                        .then(() =>
                          toast.success(
                            'All served participants have been reset'
                          )
                        )
                        .catch((error) => {
                          console.error('Failed to reset served status:', error)
                          toast.error('Failed to reset served participants')
                        })
                    }}
                    className="relative inline-flex h-10 flex-1 items-center justify-center rounded-md bg-destructive px-4 py-2 text-sm font-medium whitespace-nowrap text-destructive-foreground shadow ring-offset-background transition-colors hover:bg-destructive/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                  >
                    Yes, Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Toaster closeButton duration={10000} className="pointer-events-auto" />
    </div>
  )
}
